import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Package, CheckCircle, AlertCircle, Loader2, ScanLine, Plus, Calendar, Eye, EyeOff } from 'lucide-react';
import { stcTransService } from '../../../../../services/stcTransService';
import { NumericInput } from '../../../../common/inputs';
import { useTranslation } from '../../../../../contexts/I18nProvider';

export interface BatchCorrectionResult {
  batchNo: string;
  expiryDate?: string;
  quantity: number;
}

interface BatchCorrectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (batches: BatchCorrectionResult[]) => void;
  /** 'remove' = velg eksisterende batcher for uttak (TransType=1), 'add' = korrigering inn (TransType=2) */
  mode: 'remove' | 'add';
  itemNo: string;
  itemDescription: string;
  binCode: string;
  warehouseId: string;
  itemType: number; // 4 = batch med holdbarhet, 5 = batch med lagerstyring
}

interface AvailableBatch {
  batchNo: string;
  expiryDate: string | null;
  quantity: number;
  selectedQty: string; // string for input binding
  selected: boolean;
}

interface ExistingBatchForAdd {
  batchNo: string;
  expiryDate: string | null;
  currentQty: number;
  correctionQty: string; // string for input binding — qty to ADD
}

export const BatchCorrectionModal: React.FC<BatchCorrectionModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  mode,
  itemNo,
  itemDescription,
  binCode,
  warehouseId,
  itemType
}) => {
  const { t } = useTranslation();
  // Felles state
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevIsOpenRef = useRef(false);
  const [loading, setLoading] = useState(false);

  // Remove-modus state
  const [availableBatches, setAvailableBatches] = useState<AvailableBatch[]>([]);
  const [filteredBatches, setFilteredBatches] = useState<AvailableBatch[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Add-modus state
  const [existingBatchesForAdd, setExistingBatchesForAdd] = useState<ExistingBatchForAdd[]>([]);
  const [newBatches, setNewBatches] = useState<BatchCorrectionResult[]>([]);
  const [showAddNew, setShowAddNew] = useState(false);
  const [newBatchNo, setNewBatchNo] = useState('');
  const [newExpiryDate, setNewExpiryDate] = useState('');
  const [newQuantity, setNewQuantity] = useState('');
  const addBatchInputRef = useRef<HTMLInputElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setError(null);
      setConfirming(false);
      setSearchTerm('');
      setAvailableBatches([]);
      setFilteredBatches([]);
      setExistingBatchesForAdd([]);
      setNewBatches([]);
      setShowAddNew(false);
      setNewBatchNo('');
      setNewExpiryDate('');
      setNewQuantity('');

      // Begge moduser laster eksisterende batcher
      loadBatches();

      setTimeout(() => inputRef.current?.focus(), 100);
      setTimeout(() => inputRef.current?.focus(), 300);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter batches for remove-modus
  useEffect(() => {
    if (mode !== 'remove') return;
    if (!searchTerm.trim()) {
      setFilteredBatches(availableBatches);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredBatches(
        availableBatches.filter(b => b.batchNo.toLowerCase().includes(term))
      );
    }
  }, [searchTerm, availableBatches, mode]);

  // Fokus add-input når den vises
  useEffect(() => {
    if (showAddNew) {
      setTimeout(() => addBatchInputRef.current?.focus(), 100);
    }
  }, [showAddNew]);

  const loadBatches = async () => {
    setLoading(true);
    setError(null);
    try {
      const batches = await stcTransService.getBatchesAtBin(itemNo, warehouseId, binCode);

      if (mode === 'remove') {
        const mapped: AvailableBatch[] = batches.map(b => ({
          batchNo: b.batchNo,
          expiryDate: b.expiryDate,
          quantity: b.quantity,
          selectedQty: '',
          selected: false
        }));
        setAvailableBatches(mapped);
        setFilteredBatches(mapped);
        if (mapped.length === 0) {
          setError(t('warehouse.modules.inventoryCorrection.serialBatch.noBatchesAvailableAtLocation', {}, 'Ingen batcher tilgjengelig på denne lokasjonen'));
        }
      } else {
        // Add-modus: vis eksisterende batcher med korreksjons-qty felt
        const mapped: ExistingBatchForAdd[] = batches.map(b => ({
          batchNo: b.batchNo,
          expiryDate: b.expiryDate,
          currentQty: b.quantity,
          correctionQty: ''  // tom = ingen korreksjon
        }));
        setExistingBatchesForAdd(mapped);
      }
    } catch (err: any) {
      console.error('Error loading batches:', err);
      setError(err.message || t('warehouse.modules.inventoryCorrection.serialBatch.couldNotFetchBatches', {}, 'Kunne ikke hente batcher'));
    } finally {
      setLoading(false);
    }
  };

  // --- Remove-modus logikk ---
  const toggleBatch = (batchNo: string) => {
    setAvailableBatches(prev =>
      prev.map(b =>
        b.batchNo === batchNo ? { ...b, selected: !b.selected } : b
      )
    );
    setFilteredBatches(prev =>
      prev.map(b =>
        b.batchNo === batchNo ? { ...b, selected: !b.selected } : b
      )
    );
  };

  const updateSelectedQty = (batchNo: string, value: string) => {
    const update = (b: AvailableBatch) =>
      b.batchNo === batchNo ? { ...b, selectedQty: value, selected: true } : b;
    setAvailableBatches(prev => prev.map(update));
    setFilteredBatches(prev => prev.map(update));
  };

  const handleRemoveScanInput = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && searchTerm.trim()) {
      e.preventDefault();
      const exactMatch = availableBatches.find(
        b => b.batchNo.toLowerCase() === searchTerm.trim().toLowerCase()
      );
      if (exactMatch) {
        toggleBatch(exactMatch.batchNo);
        setSearchTerm('');
        setError(null);
      } else {
        setError(t('warehouse.modules.inventoryCorrection.serialBatch.batchNotAtLocation', {}, 'Batchnummeret finnes ikke på denne lokasjonen'));
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // --- Add-modus logikk ---
  const updateExistingCorrectionQty = (batchNo: string, value: string) => {
    setExistingBatchesForAdd(prev =>
      prev.map(b => b.batchNo === batchNo ? { ...b, correctionQty: value } : b)
    );
  };

  const addNewBatch = useCallback(() => {
    setError(null);

    // Validering basert på itemType
    if (itemType === 5 && !newBatchNo.trim()) {
      setError(t('warehouse.modules.inventoryCorrection.serialBatch.batchNoRequiredForType', {}, 'Batchnummer er påkrevd for denne varetypen'));
      return;
    }
    if (itemType === 4 && !newExpiryDate) {
      setError(t('warehouse.modules.inventoryCorrection.serialBatch.expiryRequiredForType', {}, 'Utløpsdato er påkrevd for denne varetypen'));
      return;
    }

    const batchNo = itemType === 4 && !newBatchNo.trim()
      ? newExpiryDate.replace(/-/g, '') // Auto-gen: YYYYMMDD
      : newBatchNo.trim();

    if (!batchNo) {
      setError(t('warehouse.modules.inventoryCorrection.serialBatch.batchNoRequired', {}, 'Batchnummer er påkrevd'));
      return;
    }

    const qty = parseFloat(newQuantity);
    if (isNaN(qty) || qty <= 0) {
      setError(t('warehouse.modules.inventoryCorrection.serialBatch.quantityMustBePositive', {}, 'Antall må være større enn 0'));
      return;
    }

    // Sjekk om batchen allerede er i eksisterende liste
    if (existingBatchesForAdd.some(b => b.batchNo.toLowerCase() === batchNo.toLowerCase())) {
      setError(t('warehouse.modules.inventoryCorrection.serialBatch.batchAlreadyInList', { '0': batchNo }, `Batch "${batchNo}" finnes allerede i listen. Bruk korreksjonsfeltet.`));
      return;
    }

    // Duplikatsjekk i nye batcher — slå sammen qty
    const existingIndex = newBatches.findIndex(
      b => b.batchNo.toLowerCase() === batchNo.toLowerCase()
    );
    if (existingIndex >= 0) {
      setNewBatches(prev => prev.map((b, i) =>
        i === existingIndex ? { ...b, quantity: b.quantity + qty } : b
      ));
    } else {
      setNewBatches(prev => [...prev, {
        batchNo,
        expiryDate: newExpiryDate || undefined,
        quantity: qty
      }]);
    }

    setNewBatchNo('');
    setNewExpiryDate('');
    setNewQuantity('');
    setTimeout(() => addBatchInputRef.current?.focus(), 50);
  }, [newBatchNo, newExpiryDate, newQuantity, itemType, existingBatchesForAdd, newBatches, t]);

  const handleAddKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      addNewBatch();
    }
  };

  const removeNewBatch = (index: number) => {
    setNewBatches(prev => prev.filter((_, i) => i !== index));
    setError(null);
  };

  // --- Formattering ---
  const formatExpiryDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('no-NO');
    } catch {
      return dateStr;
    }
  };

  const isExpired = (dateStr: string | null | undefined): boolean => {
    if (!dateStr) return false;
    try { return new Date(dateStr) < new Date(); } catch { return false; }
  };

  const isSoonExpired = (dateStr: string | null | undefined): boolean => {
    if (!dateStr) return false;
    try {
      const d = new Date(dateStr);
      const now = new Date();
      const thirtyDays = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      return d > now && d <= thirtyDays;
    } catch { return false; }
  };

  // --- Bekreft ---
  const handleConfirm = () => {
    if (mode === 'remove') {
      const selected = availableBatches.filter(b => b.selected);
      if (selected.length === 0) return;

      // Valider qty
      for (const b of selected) {
        const qty = parseFloat(b.selectedQty);
        if (isNaN(qty) || qty <= 0) {
          setError(t('warehouse.modules.inventoryCorrection.serialBatch.invalidQuantityForBatch', { '0': b.batchNo }, `Ugyldig antall for batch ${b.batchNo}`));
          return;
        }
        if (qty > b.quantity) {
          setError(t('warehouse.modules.inventoryCorrection.serialBatch.quantityExceedsAvailable', { '0': b.batchNo, '1': b.quantity }, `Antall for batch ${b.batchNo} kan ikke overstige tilgjengelig (${b.quantity})`));
          return;
        }
      }

      onComplete(selected.map(b => ({
        batchNo: b.batchNo,
        expiryDate: b.expiryDate || undefined,
        quantity: parseFloat(b.selectedQty)
      })));
    } else {
      // Add-modus: samle eksisterende med korreksjon + nye batcher
      const results: BatchCorrectionResult[] = [];

      // Eksisterende batcher med korreksjon > 0
      for (const b of existingBatchesForAdd) {
        const qty = parseFloat(b.correctionQty);
        if (!isNaN(qty) && qty > 0) {
          results.push({
            batchNo: b.batchNo,
            expiryDate: b.expiryDate || undefined,
            quantity: qty
          });
        }
      }

      // Nye batcher
      results.push(...newBatches);

      if (results.length === 0) return;
      onComplete(results);
    }
  };

  const stopPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  if (!isOpen) return null;

  const isRemove = mode === 'remove';
  const existingWithQty = existingBatchesForAdd.filter(b => {
    const qty = parseFloat(b.correctionQty);
    return !isNaN(qty) && qty > 0;
  }).length;
  const batchCount = isRemove
    ? availableBatches.filter(b => b.selected).length
    : existingWithQty + newBatches.length;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-0 sm:p-4"
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-lg sm:max-h-[90vh] flex flex-col overflow-hidden"
        onClick={stopPropagation}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${isRemove ? 'bg-red-50' : 'bg-orange-50'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isRemove ? 'bg-red-100' : 'bg-orange-100'}`}>
              <Package className={isRemove ? 'text-red-600' : 'text-orange-600'} size={20} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isRemove ? t('warehouse.modules.inventoryCorrection.serialBatch.selectBatchesForRemoval', {}, 'Velg batcher for uttak') : t('warehouse.modules.inventoryCorrection.serialBatch.correctBatchesIn', {}, 'Korriger batcher inn')}
              </h2>
              <p className="text-sm text-gray-500">{itemNo} - {itemDescription}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Item/Location info */}
        <div className="px-4 pt-3 pb-2">
          <div className="flex items-center gap-4 text-sm text-gray-600">
            <span>{t('warehouse.general.location')}: <span className="font-medium text-gray-900">{binCode}</span></span>
            {availableBatches.length > 0 && isRemove && (
              <span>{t('warehouse.modules.inventoryCorrection.serialBatch.batchesLabel', {}, 'Batcher')}: <span className="font-medium text-gray-900">{availableBatches.length}</span></span>
            )}
            {!isRemove && existingBatchesForAdd.length > 0 && (
              <span>{t('warehouse.modules.inventoryCorrection.serialBatch.existingLabel', {}, 'Eksisterende')}: <span className="font-medium text-gray-900">{existingBatchesForAdd.length}</span></span>
            )}
          </div>
          {!isRemove && (
            <p className="text-xs text-gray-400 mt-1">
              {t('warehouse.modules.inventoryCorrection.serialBatch.addQtyPerBatchHint', {}, 'Angi antall å legge inn per batch, eller legg til nye batcher.')}
            </p>
          )}
        </div>

        {/* Search (remove) / Error display */}
        <div className="px-4 pb-3">
          {isRemove && (
            <div className="relative">
              <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setError(null); }}
                onKeyDown={handleRemoveScanInput}
                placeholder={t('warehouse.modules.inventoryCorrection.serialBatch.scanOrSearchBatch', {}, 'Skann eller søk batchnummer...')}
                className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                autoComplete="off"
              />
            </div>
          )}

          {error && (
            <div className="mt-2 flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-lg p-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 pb-2">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-gray-500 py-8">
              <Loader2 className="animate-spin" size={20} />
              {t('warehouse.modules.inventoryCorrection.serialBatch.fetchingBatches', {}, 'Henter batcher...')}
            </div>
          ) : isRemove ? (
            /* Remove-modus: Batch-liste med checkboxes og qty-input */
            filteredBatches.length === 0 && !error ? (
              <div className="text-center text-gray-500 py-8">
                {searchTerm ? t('warehouse.modules.inventoryCorrection.serialBatch.noSearchMatch', {}, 'Ingen treff på søket') : t('warehouse.modules.inventoryCorrection.serialBatch.noBatchesAvailable', {}, 'Ingen batcher tilgjengelig')}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredBatches.map((batch) => (
                  <div
                    key={batch.batchNo}
                    className={`p-3 rounded-lg border transition-all ${
                      batch.selected
                        ? 'border-red-500 bg-red-50 ring-2 ring-red-200'
                        : 'border-gray-200 hover:border-red-300 hover:bg-red-50/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => toggleBatch(batch.batchNo)}
                        className="flex items-center gap-3 text-left flex-1"
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          batch.selected ? 'bg-red-500 border-red-500' : 'border-gray-300'
                        }`}>
                          {batch.selected && <CheckCircle className="text-white" size={14} />}
                        </div>
                        <div>
                          <span className="font-mono font-medium text-gray-900">{batch.batchNo}</span>
                          {batch.expiryDate && (
                            <div className="flex items-center gap-1 mt-0.5">
                              <Calendar size={11} className="text-gray-400" />
                              <span className="text-xs text-gray-500">{formatExpiryDate(batch.expiryDate)}</span>
                              {isExpired(batch.expiryDate) && (
                                <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium">{t('warehouse.modules.inventoryCorrection.serialBatch.expired', {}, 'Utløpt')}</span>
                              )}
                              {isSoonExpired(batch.expiryDate) && (
                                <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-medium">{t('warehouse.modules.inventoryCorrection.serialBatch.expiringSoon', {}, 'Snart utløpt')}</span>
                              )}
                            </div>
                          )}
                          <span className="text-xs text-gray-500">{t('warehouse.modules.inventoryCorrection.serialBatch.stockLabel', {}, 'Beholdning')}: {batch.quantity}</span>
                        </div>
                      </button>

                      {/* Qty-input for uttak */}
                      {batch.selected && (
                        <div className="flex items-center gap-2 ml-2">
                          <label className="text-xs text-gray-500">{t('common.fields.quantity')}:</label>
                          <NumericInput
                            mode="decimal"
                            value={batch.selectedQty}
                            onChange={(e) => updateSelectedQty(batch.batchNo, e.target.value)}
                            className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:ring-1 focus:ring-red-500 focus:border-red-500"
                            min="0.01"
                            max={batch.quantity}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            /* Add-modus: Eksisterende batcher med korreksjons-qty + nye batcher */
            <>
              {/* Eksisterende batcher */}
              {existingBatchesForAdd.length > 0 && (
                <div className="space-y-2 mb-3">
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                    {t('warehouse.modules.inventoryCorrection.serialBatch.existingBatches', { '0': existingBatchesForAdd.length }, `Eksisterende batcher (${existingBatchesForAdd.length})`)}
                  </div>
                  {existingBatchesForAdd.map((batch) => (
                    <div
                      key={batch.batchNo}
                      className="p-3 rounded-lg border border-green-300 bg-green-50"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <span className="font-mono font-medium text-gray-900">{batch.batchNo}</span>
                          {batch.expiryDate && (
                            <div className="flex items-center gap-1 mt-0.5">
                              <Calendar size={11} className="text-gray-400" />
                              <span className="text-xs text-gray-500">{formatExpiryDate(batch.expiryDate)}</span>
                              {isExpired(batch.expiryDate) && (
                                <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium">{t('warehouse.modules.inventoryCorrection.serialBatch.expired', {}, 'Utløpt')}</span>
                              )}
                              {isSoonExpired(batch.expiryDate) && (
                                <span className="text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-medium">{t('warehouse.modules.inventoryCorrection.serialBatch.expiringSoon', {}, 'Snart utløpt')}</span>
                              )}
                            </div>
                          )}
                          <span className="text-xs text-gray-500">{t('warehouse.modules.inventoryCorrection.serialBatch.currentLabel', {}, 'Nåværende')}: {batch.currentQty}</span>
                        </div>

                        <div className="flex items-center gap-2 ml-2">
                          <label className="text-xs text-gray-500">{t('warehouse.modules.inventoryCorrection.serialBatch.addLabel', {}, 'Legg til')}:</label>
                          <NumericInput
                            mode="decimal"
                            value={batch.correctionQty}
                            onChange={(e) => updateExistingCorrectionQty(batch.batchNo, e.target.value)}
                            placeholder="0"
                            className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm text-right focus:ring-1 focus:ring-orange-500 focus:border-orange-500"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Nye batcher */}
              {newBatches.length > 0 && (
                <div className="space-y-1.5 mb-3">
                  <div className="text-xs font-medium text-orange-600 uppercase tracking-wider mb-2">
                    {t('warehouse.modules.inventoryCorrection.serialBatch.newBatches', { '0': newBatches.length }, `Nye batcher (${newBatches.length})`)}
                  </div>
                  {newBatches.map((nb, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 rounded-lg border border-orange-300 bg-orange-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded bg-orange-500 border-2 border-orange-500 flex items-center justify-center">
                          <Plus className="text-white" size={12} />
                        </div>
                        <div>
                          <span className="font-mono font-medium text-gray-900">{nb.batchNo}</span>
                          {nb.expiryDate && (
                            <span className="text-xs text-gray-500 ml-2">
                              <Calendar size={11} className="inline mr-0.5" />
                              {formatExpiryDate(nb.expiryDate)}
                            </span>
                          )}
                          <span className="text-xs text-gray-500 ml-2">{t('common.fields.quantity')}: {nb.quantity}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => removeNewBatch(index)}
                        className="text-orange-400 hover:text-red-500 text-xs transition-colors"
                      >
                        {t('common.buttons.remove')}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {existingBatchesForAdd.length === 0 && newBatches.length === 0 && (
                <div className="text-center py-8 text-gray-400">
                  <Package size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">{t('warehouse.modules.inventoryCorrection.serialBatch.noBatchesFoundAtLocation', {}, 'Ingen batcher funnet på lokasjonen')}</p>
                  <p className="text-xs mt-1">{t('warehouse.modules.inventoryCorrection.serialBatch.useAddNewBatchHint', {}, 'Bruk "Legg til ny batch" for å registrere')}</p>
                </div>
              )}

              {/* Legg til ny batch-seksjon */}
              <div className="mt-3 border-t pt-3">
                <button
                  onClick={() => setShowAddNew(!showAddNew)}
                  className="flex items-center gap-2 text-sm text-orange-600 hover:text-orange-800 font-medium transition-colors"
                >
                  {showAddNew ? <EyeOff size={16} /> : <Eye size={16} />}
                  {showAddNew ? t('warehouse.modules.inventoryCorrection.serialBatch.hide', {}, 'Skjul') : t('warehouse.modules.inventoryCorrection.serialBatch.addNewBatch', {}, 'Legg til ny batch')}
                </button>
                {showAddNew && (
                  <div className="mt-3 space-y-2 bg-orange-50 rounded-lg p-3 border border-orange-200">
                    {/* BatchNo */}
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">
                        {t('warehouse.modules.inventoryCorrection.serialBatch.batchNoLabel', {}, 'Batchnummer')} {itemType === 5 ? t('warehouse.modules.inventoryCorrection.serialBatch.required', {}, '(påkrevd)') : t('warehouse.modules.inventoryCorrection.serialBatch.optionalGenFromExpiry', {}, '(valgfritt — genereres fra utløpsdato)')}
                      </label>
                      <input
                        ref={addBatchInputRef}
                        type="text"
                        value={newBatchNo}
                        onChange={(e) => { setNewBatchNo(e.target.value); setError(null); }}
                        onFocus={(e) => e.stopPropagation()}
                        placeholder={itemType === 4 ? t('warehouse.modules.inventoryCorrection.serialBatch.autoFromExpiry', {}, 'Auto fra utløpsdato...') : t('warehouse.modules.inventoryCorrection.serialBatch.scanBatchNo', {}, 'Skann batchnummer...')}
                        className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                        autoComplete="off"
                      />
                    </div>

                    {/* ExpiryDate */}
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">
                        {t('warehouse.modules.inventoryCorrection.serialBatch.expiryDateLabel', {}, 'Utløpsdato')} {itemType === 4 ? t('warehouse.modules.inventoryCorrection.serialBatch.required', {}, '(påkrevd)') : t('warehouse.modules.inventoryCorrection.serialBatch.optional', {}, '(valgfritt)')}
                      </label>
                      <input
                        type="date"
                        value={newExpiryDate}
                        onChange={(e) => { setNewExpiryDate(e.target.value); setError(null); }}
                        className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      />
                    </div>

                    {/* Quantity */}
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1 block">{t('common.fields.quantity')}</label>
                      <NumericInput
                        mode="decimal"
                        value={newQuantity}
                        onChange={(e) => { setNewQuantity(e.target.value); setError(null); }}
                        onKeyDown={handleAddKeyDown}
                        placeholder="0"
                        min="0.01"
                        className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                      />
                    </div>

                    <button
                      onClick={addNewBatch}
                      disabled={!newQuantity.trim()}
                      className="w-full px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-1 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Plus size={14} />
                      {t('warehouse.modules.inventoryCorrection.serialBatch.addBatch', {}, 'Legg til batch')}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('common.buttons.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={batchCount === 0 || confirming}
            className={`px-5 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
              batchCount > 0
                ? isRemove
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-orange-600 text-white hover:bg-orange-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {confirming ? (
              <Loader2 className="animate-spin" size={16} />
            ) : (
              <CheckCircle size={16} />
            )}
            {isRemove
              ? t('warehouse.modules.inventoryCorrection.serialBatch.confirmBatchRemoval', { '0': batchCount }, `Bekreft uttak (${batchCount} batcher)`)
              : t('warehouse.modules.inventoryCorrection.serialBatch.confirmBatchAdd', { '0': batchCount }, `Bekreft innlegging (${batchCount} batcher)`)
            }
          </button>
        </div>
      </div>
    </div>
  );
};
